/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useEffect, useState, useRef } from 'react';
import {
	getATLReact,
	getATLReactMetrics,
	disableATLReactMetrics,
	type AtlReactMetrics,
} from '@jira/platform__atlassian-react/src/atl-react.tsx';
import type { ErrorInfo } from 'react-dom/client';
import { useIsHydrateRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { RenderAfterGlobalHydrationCompletion } from '@jira/ssr__global-hydration-completeness/src/RenderAfterGlobalHydrationCompletion.tsx';
import { unwrapFiberToOwnershipTrace } from '@jira/platform__react-fiber/src/unwrapFiberToOwnershipTrace.tsx';
import { getIsGlobalTimedOut } from '@jira/ssr__global-hydration-completeness/src/useIsGlobalHydrationComplete.tsx';
import { captureException } from '@jira/platform__observability-capture-exception/src/index.tsx';
import { BackgroundPriority } from '@jira/platform__services-non-critical/src/BackgroundPriority.tsx';
import type { ReactHydrationStats } from '@atlaskit/react-ufo/config';
import { createRouterSelector } from '@atlassian/react-resource-router';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	getRegionPreservationMetrics,
	type RegionHydrationReport,
	setupRegionReporters,
} from './context-preservation-report.tsx';
import { hydrationDiffer } from './hydrationDiffer.tsx';

type HydrationStats = {
	enabled: boolean;
	hasFatalError: boolean;
	warningCount: number;
	minErrorComponentDepth: number | null;
	maxErrorComponentDepth: number | null;
	reporters: RegionHydrationReport | null;
};

const HYDRATION_STATS: HydrationStats = {
	enabled: false,
	hasFatalError: false,
	warningCount: 0,
	minErrorComponentDepth: null,
	maxErrorComponentDepth: null,
	reporters: null,
};

const useRouteName = createRouterSelector((state) => state.route.name);

const inRange = (min: number, max: number, value: number) => Math.max(min, Math.min(max, value));

const getQualityMetrics = ({
	componentMountWorkCount,
	componentUpdateWorkCount,
	totalCommitCount,
	priorityInterruptedWorkCount,
}: AtlReactMetrics): {
	renderFactor: number;
	restartFactor: number;
	complexityFactor: number;
} => ({
	// render factor as simple number of total "attempts" to render something
	renderFactor: inRange(0, 10, 10 * Math.round(componentUpdateWorkCount / componentMountWorkCount)),
	// restart factor as proportion of work started and work done
	restartFactor: inRange(
		0,
		10,
		Math.round((10 * priorityInterruptedWorkCount) / componentMountWorkCount),
	),
	// complexity raising every 50 commits (100 is "norm")
	complexityFactor: inRange(0, 10, Math.round(totalCommitCount / 50)),
});

const ReportMetrics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const initialRouteName = useRouteName();
	// Using state here because we want to capture the route name on initial render, and not have it change when the effect runs
	const [routeName] = useState(initialRouteName);
	const isHydrateMode = useIsHydrateRenderMode();

	useEffect(() => {
		const metrics = getATLReactMetrics();
		if (!metrics) {
			return;
		}
		// https://data-portal.internal.atlassian.com/analytics/registry/94211
		fireOperationalAnalytics(createAnalyticsEvent({}), 'reactMetric mount', {
			routeName,
			hydration: isHydrateMode,
			...metrics,
		});

		if (fg('jfp_magma_jira-report-render-quality')) {
			// data-portal.internal.atlassian.com/analytics/registry/102068
			fireOperationalAnalytics(createAnalyticsEvent({}), 'reactMetric quality', {
				routeName,
				...getQualityMetrics(metrics),
			});
		}

		// and disable them 🤫
		disableATLReactMetrics();
	}, [createAnalyticsEvent, routeName, isHydrateMode]);

	return null;
};

export const reportHydrationDifference = (routeName: string): void => {
	const { reporters } = HYDRATION_STATS;
	if (!reporters || !fg('jfp_magma_jira-report-hydration-mismatch')) {
		return;
	}
	const rootReport = reporters.root?.();
	if (!rootReport?.removed) {
		return;
	}
	const sharedStats = {
		routeName,
		nodesRemoved: rootReport.removed,
		boundariesRemoved: rootReport.failedBoundaries,
	};
	const diff = hydrationDiffer(rootReport);
	if (!diff) {
		log.safeErrorWithoutCustomerData('react-hydration-mismatch', 'difference cannot be detected', {
			...sharedStats,
		});
		return;
	}

	const { liveAnchor, serverNode, clientNode, kind } = diff;
	const trace = liveAnchor && unwrapFiberToOwnershipTrace(liveAnchor, serverNode, clientNode);
	if (!trace) {
		log.safeErrorWithoutCustomerData(
			'react-hydration-mismatch',
			`trace for ${kind} cannot be expanded`,
			{
				...sharedStats,
				kind,
			},
		);
		return;
	}
	log.safeErrorWithoutCustomerData('react-hydration-mismatch', `${kind} -> ${trace.cause}`, {
		...sharedStats,
		kind,
		domPath: trace.domPath,
		location: trace.summary.join('\n'),
	});
};

const ReportHydration = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const initialRouteName = useRouteName();
	// Using state here because we want to capture the route name on initial render, and not have it change when the effect runs
	const [routeName] = useState(initialRouteName);
	const reported = useRef(false);

	useEffect(() => {
		// disable lanes alternation after hydration is complete
		getATLReact().__ATL_SYNC_HYDRATION_PRIORITY = false;
	}, []);

	useEffect(() => {
		if (!HYDRATION_STATS.enabled || reported.current) {
			return;
		}
		reported.current = true;

		const {
			hasFatalError,
			warningCount,
			minErrorComponentDepth,
			maxErrorComponentDepth,
			reporters,
		} = HYDRATION_STATS;
		const successful = !hasFatalError;

		const hydrationPreservationReport = reporters
			? (() => {
					const preservation = getRegionPreservationMetrics(reporters);

					const TOPNAV_KEY = 'page-layout.top-nav';
					const SIDEBAR_KEY = 'page-layout.sidebar';
					const MAIN_KEY = 'page-layout.main';
					// track only top regions
					const topNav = preservation[TOPNAV_KEY] ?? undefined;
					const sideBar = preservation[SIDEBAR_KEY] ?? undefined;
					const main = preservation[MAIN_KEY] ?? undefined;

					return {
						topNavPreservation: topNav,
						sideBarPreservation: sideBar,
						mainPreservation: main,

						topNavUnmounted: reporters[TOPNAV_KEY]?.().unmounted ?? 0,
						sidebarUnmounted: reporters[SIDEBAR_KEY]?.().unmounted ?? 0,
						mainUnmounted: reporters[MAIN_KEY]?.().unmounted ?? 0,

						topNavRemoved: reporters[TOPNAV_KEY]?.().removed ?? 0,
						sidebarRemoved: reporters[SIDEBAR_KEY]?.().removed ?? 0,
						mainRemoved: reporters[MAIN_KEY]?.().removed ?? 0,

						topNavAdded: reporters[TOPNAV_KEY]?.().added ?? 0,
						sidebarAdded: reporters[SIDEBAR_KEY]?.().added ?? 0,
						mainAdded: reporters[MAIN_KEY]?.().added ?? 0,
					};
				})()
			: undefined;

		// data-portal.internal.atlassian.com/analytics/registry/87189
		fireOperationalAnalytics(
			createAnalyticsEvent({}),
			successful ? 'reactHydrate success' : 'reactHydrate failure',
			{
				warningCount: bucketedWarningCount(warningCount),
				timedOut: getIsGlobalTimedOut(),
				routeName,
			},
		);

		const paretoBuckets = {
			topNavPreservationBucket: paretoBucket(hydrationPreservationReport?.topNavPreservation ?? 1),
			sideBarPreservationBucket: paretoBucket(
				hydrationPreservationReport?.sideBarPreservation ?? 1,
			),
			mainPreservationBucket: paretoBucket(hydrationPreservationReport?.mainPreservation ?? 1),
		};
		// https://data-portal.internal.atlassian.com/analytics/registry/93805
		fireOperationalAnalytics(createAnalyticsEvent({}), 'reactHydrate status', {
			successful,
			routeName,
			...paretoBuckets,
			...hydrationPreservationReport,
		});

		reportHydrationDifference(routeName);

		log.safeInfoWithoutCustomerData(
			'react-hydration-reporting',
			successful ? 'Hydration success' : 'Hydration failure',
			{
				warningCount,
				routeName,
				minErrorComponentDepth,
				maxErrorComponentDepth,
				...hydrationPreservationReport,
			},
		);
	}, [createAnalyticsEvent, routeName]);

	return null;
};

/**
 * Reports the overall success / failure of React hydration once complete. This is inside a React component so that it
 * can access analytics context and the current route.
 */
export const HydrationStatsReporter = (): React.JSX.Element => (
	<>
		<RenderAfterGlobalHydrationCompletion>
			<ReportHydration />
		</RenderAfterGlobalHydrationCompletion>
		<BackgroundPriority>
			<ReportMetrics />
		</BackgroundPriority>
	</>
);

export const getReactHydrationStats = (): ReactHydrationStats | undefined => {
	if (!HYDRATION_STATS.enabled) {
		return undefined;
	}
	return {
		successful: !HYDRATION_STATS.hasFatalError,
		warningCount: HYDRATION_STATS.warningCount,
		minWarningComponentDepth: HYDRATION_STATS.minErrorComponentDepth ?? undefined,
		maxWarningComponentDepth: HYDRATION_STATS.maxErrorComponentDepth ?? undefined,
		preservationRatio: HYDRATION_STATS.reporters?.root?.().preservationRatio,
	};
};

export const bucketedWarningCount = (warningCount: number): string => {
	if (warningCount <= 9) {
		return warningCount.toString();
	}
	return '10+';
};

/**
 * buckets a score into Pareto(80/20) buckets. Total 13
 * 10, 30, 50, 70, 80, 85, 90, 92, 94, 96, 98, 99, 100.
 * It "spends" more at the high end (90-100) where we care more about precision.
 */
export const paretoBucket = (value: number): number => {
	const clamped = Math.max(0, Math.min(100, Math.round(value)));

	// 0-79: coarser buckets (20-point)
	if (clamped < 20) return 10;
	if (clamped < 40) return 30;
	if (clamped < 60) return 50;
	if (clamped < 80) return 70;

	// 80-89: 5-point buckets
	if (clamped < 85) return 80;
	if (clamped < 90) return 85;

	// 90-97: 2-point buckets
	if (clamped < 92) return 90;
	if (clamped < 94) return 92;
	if (clamped < 96) return 94;
	if (clamped < 98) return 96;

	// 98-100: 1-point buckets (critical zone)
	if (clamped < 99) return 98;
	if (clamped < 100) return 99;
	return 100;
};

/**
 * Enables high-level hydration stats based on hydration error logs.
 */
export const enableHydrationStatReporting = (rootContainer: HTMLElement): void => {
	HYDRATION_STATS.enabled = true;

	try {
		HYDRATION_STATS.reporters = setupRegionReporters(
			rootContainer,
			fg('jfp_magma_jira-report-hydration-mismatch'),
		);
	} catch (error) {
		captureException(
			'content-preservation',
			new Error('Content preservation failed to capture initial state', { cause: error }),
		);
	}
};

/**
 * To be called for each error from React hydrateRoot#onRecoverableError which may be a hydration error.
 */
export const addErrorForHydrationStats = (error: Error, errorInfo: ErrorInfo): void => {
	switch (classifyAsHydrationError(error)) {
		case HYDRATION_FATAL_ERROR:
			HYDRATION_STATS.hasFatalError = true;
			recordComponentDepthStats(errorInfo);
			break;
		case HYDRATION_WARNING:
			HYDRATION_STATS.warningCount++;
			recordComponentDepthStats(errorInfo);
			break;
		default:
			break;
	}
};

const HYDRATION_FATAL_ERROR = 'fatal';
const HYDRATION_WARNING = 'warning';
type HydrationErrorType = typeof HYDRATION_FATAL_ERROR | typeof HYDRATION_WARNING;
/**
 * Determines if the error is a hydration error and if so, what type.
 * This is fragile because it depends on the exact React version's error messages (18.2.0), defined here:
 * https://github.com/facebook/react/blob/v18.2.0/scripts/error-codes/codes.json
 * To do this properly requires patching React (again). Using best-effort detection like this is the less-bad option.
 */
export const classifyAsHydrationError = ({ message }: Error): HydrationErrorType | null => {
	if (process.env.NODE_ENV === 'production') {
		if (PROD_FATAL_MESSAGES.some((fatal) => message.includes(fatal))) {
			return HYDRATION_FATAL_ERROR;
		}
		if (PROD_WARNING_MESSAGES.some((warning) => message.includes(warning))) {
			return HYDRATION_WARNING;
		}
	} else {
		if (DEV_FATAL_MESSAGES.some((fatal) => message.includes(fatal))) {
			return HYDRATION_FATAL_ERROR;
		}
		if (DEV_WARNING_MESSAGES.some((warning) => message.includes(warning))) {
			return HYDRATION_WARNING;
		}
	}

	return null;
};

// We ignore 419 "The server could not finish this Suspense boundary, likely due to an error during server rendering. Switched to client rendering."
// because we *expect* these to happen when we throw to a suspense boundary e.g. due to timeout
const PROD_FATAL_MESSAGES = [407, 421, 422, 423, 424].map(
	(code) => `Minified React error #${code}`,
);
const PROD_WARNING_MESSAGES = [418, 425].map((code) => `Minified React error #${code}`);
const DEV_FATAL_MESSAGES = ['to client rendering'];
const DEV_WARNING_MESSAGES = [
	'Hydration failed because the initial UI does not match what was rendered on the server',
	'Text content does not match server-rendered HTML',
];

const recordComponentDepthStats = ({ componentStack }: ErrorInfo) => {
	if (!componentStack) {
		return;
	}
	// Number of components = number of lines after the error message
	const componentStackDepth = componentStack.match(/\n/g)?.length ?? 1;
	HYDRATION_STATS.minErrorComponentDepth = Math.min(
		componentStackDepth,
		HYDRATION_STATS.minErrorComponentDepth ?? Number.MAX_SAFE_INTEGER,
	);
	HYDRATION_STATS.maxErrorComponentDepth = Math.max(
		componentStackDepth,
		HYDRATION_STATS.maxErrorComponentDepth ?? 0,
	);
};
